import { useContext, useMemo, useState } from 'react';
import { Route } from 'react-router-dom';
import useSWR from 'swr';
import { ApiKey } from '../../../../../../../../../typings/ApiKey.interface';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import { isUnauthorizedError } from '../../../../../../client/Hookdeck';
import LINKS from '../../../../../../configs/links';
import Button from '../../../../../common/base/Button';
import Container from '../../../../../common/base/Container';
import Icon from '../../../../../common/base/Icon';
import Link from '../../../../../common/base/Link';
import Text from '../../../../../common/base/Text';
import Search from '../../../../../common/Search';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import { useApiKeyActions } from '../../../../../hooks/useApiKeyActions';
import { DashboardContext } from '../../../DashboardContext';
import AddOrganizationApiKeyModal from './AddOrganizationApiKeyModal';
import AddProjectApiKeyModal from './AddProjectApiKeyModal';
import { OrganizationApiKeysTable } from './OrganizationApiKeysTable';
import UpdateOrganizationApiKeyModal from './UpdateOrganizationApiKeyModal';
import UpdateProjectApiKeyModal from './UpdateProjectApiKeyModal';
import { ProjectApiKeysTable } from './ProjectApiKeysTable';
import { ProjectFilterDropdown } from './ProjectFilterDropdown';
import Tooltip from '../../../../../common/base/Tooltip';
export default function OrganizationApiKeys() {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    organization,
    teams
  } = useContext(DashboardContext);
  const {
    data,
    error: error_0,
    mutate
  } = useSWR(APIMethodKeys.organizations.listApiKeys(organization!.id), () => HookdeckAPI.organizations.listApiKeys(), {
    shouldRetryOnError: error => !isUnauthorizedError(error)
  });
  const api_keys = data ?? [];
  const api_keys_unauthorized = isUnauthorizedError(error_0);
  const [org_search_value, setOrgSearchValue] = useState('');
  const [project_search_value, setProjectSearchValue] = useState('');
  const [project_filter_ids, setProjectFilterIds] = useState<string[]>([]);
  const [editing_api_key, setEditingApiKey] = useState<ApiKey | null>(null);
  const [editing_project_api_key, setEditingProjectApiKey] = useState<ApiKey | null>(null);
  const project_options = useMemo(() => (teams ?? []).filter(team => team.organization_id === organization?.id).map(team_0 => ({
    id: team_0.id,
    name: team_0.name
  })), [teams, organization?.id]);
  const organization_api_key_actions = useApiKeyActions({
    managed_from: 'organization',
    onRolled: () => mutate(),
    onDeleted: id => mutate(keys => keys?.filter(key => key.id !== id), false)
  });
  const project_api_key_actions = useApiKeyActions({
    managed_from: 'organization',
    onRolled: () => mutate(),
    onDeleted: id_0 => mutate(keys_0 => keys_0?.filter(key_0 => key_0.id !== id_0), false)
  });
  const org_filtered_api_keys = org_search_value ? api_keys.filter(api_key => api_key.label?.toLowerCase().includes(org_search_value.toLowerCase()) || api_key.key?.toLowerCase().includes(org_search_value.toLowerCase())) : api_keys;
  const total_org_api_keys = api_keys.filter(api_key_0 => !api_key_0.team_id).length;
  const total_project_api_keys = api_keys.filter(api_key_1 => api_key_1.team_id).length;
  const project_filtered_api_keys = api_keys.filter(api_key_2 => {
    const matches_search = !project_search_value || api_key_2.label?.toLowerCase().includes(project_search_value.toLowerCase()) || api_key_2.key?.toLowerCase().includes(project_search_value.toLowerCase());
    const matches_project = project_filter_ids.length === 0 || api_key_2.team_id !== null && project_filter_ids.includes(api_key_2.team_id);
    return matches_search && matches_project;
  });
  return <>
      <Container large left>
        <Route path="/settings/organization/api-keys/add" render={() => <AddOrganizationApiKeyModal onApiKeyCreated={(api_key_3: ApiKey) => {
        mutate(keys_1 => [api_key_3, ...(keys_1 ?? [])], false);
      }} />} />
        <Route path="/settings/organization/api-keys/project/add" render={() => <AddProjectApiKeyModal onApiKeyCreated={(api_key_4: ApiKey) => {
        mutate(keys_2 => [api_key_4, ...(keys_2 ?? [])], false);
      }} />} />
        {editing_api_key && <UpdateOrganizationApiKeyModal api_key={editing_api_key} onClose={() => setEditingApiKey(null)} onApiKeyUpdated={(updated_api_key: ApiKey) => {
        mutate(keys_3 => keys_3?.map(key_1 => key_1.id === updated_api_key.id ? updated_api_key : key_1), false);
      }} />}
        {editing_project_api_key && <UpdateProjectApiKeyModal api_key={editing_project_api_key} managed_from="organization" onClose={() => setEditingProjectApiKey(null)} onApiKeyUpdated={(updated_api_key_0: ApiKey) => {
        mutate(keys_4 => keys_4?.map(key_2 => key_2.id === updated_api_key_0.id ? updated_api_key_0 : key_2), false);
      }} />}
        <Div m={{
        b: 10
      }}>
          <Div m={{
          b: 4
        }} flex={{
          justify: 'space-between',
          align: 'center'
        }}>
            <Div flex={{
            align: 'center',
            gap: 2
          }}>
              <Text bold size="l" as="h2" m={{
              y: 0
            }}>
                Organization API Keys
              </Text>
              <Tooltip tooltip="Manage API keys scoped to this organization.">
                <Icon icon="info" muted />
              </Tooltip>
            </Div>
            <Div flex={{
            align: 'center',
            gap: 3
          }}>
              <Search value={org_search_value} onChange={setOrgSearchValue} small disabled={api_keys_unauthorized} placeholder="Filter by name..." w={{
              px: 240
            }} />
              <Button.Permission role="admin" unauthorized_tooltip="Only Owners and Admins can create API keys." to={loc => ({
              ...loc,
              pathname: '/settings/organization/api-keys/add',
              state: {
                scroll: false
              }
            })} primary data-testid="organization-api-key-create" icon="add_circle">
                New Organization API Key
              </Button.Permission>
            </Div>
          </Div>
          <OrganizationApiKeysTable api_keys={org_filtered_api_keys} total={total_org_api_keys} unauthorized={api_keys_unauthorized} onEditKey={setEditingApiKey} onRollKey={organization_api_key_actions.handleRollApiKey} onDeleteKey={organization_api_key_actions.handleDeleteApiKey} />
        </Div>
      </Container>
      <Container large left>
        <Div m={{
        b: 4
      }}>
          <Div m={{
          b: 4
        }} flex={{
          justify: 'space-between',
          align: 'center'
        }}>
            <Div flex={{
            align: 'center',
            gap: 2
          }}>
              <Text bold size="l" as="h2" m={{
              y: 0
            }}>
                Project API Keys
              </Text>
              <Tooltip tooltip="Manage API keys scoped to projects in this organization.">
                <Icon icon="info" muted />
              </Tooltip>
            </Div>
            <Div flex={{
            align: 'center',
            gap: 3
          }}>
              <Search value={project_search_value} onChange={setProjectSearchValue} small disabled={api_keys_unauthorized} placeholder="Filter by name..." w={{
              px: 240
            }} />
              <ProjectFilterDropdown projects={project_options} selected_ids={project_filter_ids} disabled={api_keys_unauthorized} onChange={setProjectFilterIds} />
              <Button.Permission role="admin" unauthorized_tooltip="Only Owners and Admins can create API keys." to={loc_0 => ({
              ...loc_0,
              pathname: '/settings/organization/api-keys/project/add',
              state: {
                scroll: false
              }
            })} primary data-testid="organization-project-api-key-create" icon="add_circle">
                New API Key
              </Button.Permission>
            </Div>
          </Div>
          <ProjectApiKeysTable api_keys={project_filtered_api_keys} total={total_project_api_keys} unauthorized={api_keys_unauthorized} onEditKey={setEditingProjectApiKey} onRollKey={project_api_key_actions.handleRollApiKey} onDeleteKey={project_api_key_actions.handleDeleteApiKey} />
        </Div>
        <Div flex={{
        align: 'center',
        gap: 1
      }}>
          <Link neutral href={LINKS.api_ref.root} target="_blank">
            <Text semi muted>
              Learn more about the API
            </Text>
          </Link>
          <Text muted>
            <Icon icon="link" />
          </Text>
        </Div>
      </Container>
    </>;
}